import { FC } from "react"
import { css } from "@emotion/react"
import { Box, Button, ButtonProps, Divider, Heading, HeadingProps } from "@ncs/web-legos"
import { PartPreviewCardEcomm } from "."
import { useOnliePartsEcommService } from "@ncs/ncs-api/portal-apps"
import { Skeleton } from "@material-ui/lab"

interface PartPreviewRowProps {
	heading?: string
	partIds: (string | null | undefined)[]
	button?: ButtonProps
	headingVariant?: HeadingProps["variant"]
	ordering?: string
	onAddToCart?: (partId: string) => void
}

export const PartPreviewRowEcomm: FC<PartPreviewRowProps> = ({
	heading,
	partIds,
	button,
	headingVariant = "h2",
	ordering = "title",
	onAddToCart,
}) => {
	const partIdsToShow: string[] = [
		...new Set(partIds?.filter((id) => id).map((id) => String(id))),
	]

	const [customerParts, isLoadingCustomerParts] = useOnliePartsEcommService({
		params: {
			onlinePartIds: partIdsToShow,
			ordering,
		},
		queryConfig: {
			enabled: partIdsToShow.length > 0,
		},
		pageSize: 10,
	})

	if (partIdsToShow.length === 0) {
		return null
	}

	return (
		<>
			{heading && (
				<Heading variant={headingVariant} bold mt={1} mb={1.5}>
					{heading}
				</Heading>
			)}

			<Box
				d="flex"
				css={css`
					padding-left: 1em;
					padding-top: 1em;
					padding-bottom: 1em;
					max-width: 100%;
					width: 100%;
					overflow-x: auto;
					gap: 24px;
					&::-webkit-scrollbar {
						height: 4px;
					}

					&::-webkit-scrollbar-thumb {
						background-color: rgba(0, 0, 0, 0.2);
						border-radius: 2px;
					}

					&::-webkit-scrollbar-track {
						background: transparent;
					}
				`}
			>
				{isLoadingCustomerParts ?
					Array.from({ length: 5 }).map((_, index) => (
						<Box display="flex" flexDirection="column" key={index}>
							<Skeleton variant="rect" height={200} width={220} />
							<Box mt={3}>
								<Skeleton variant="text" height={44} width={220} />
							</Box>
							<Box>
								<Skeleton variant="text" height={22} width={120} />
							</Box>
							<Box mt={3}>
								<Skeleton variant="text" height={42} width={120} />
							</Box>
							<Box mt={3}>
								<Skeleton variant="text" height={"52px"} width={220} />
							</Box>
						</Box>
					))
				:	customerParts?.map((part: any) => (
						<Box
							key={part.id}
							css={css`
								flex-shrink: 0;
							`}
						>
							<PartPreviewCardEcomm part={part} onAddToCart={onAddToCart} />
						</Box>
					))
				}
			</Box>

			{button && (
				<Box mt={2}>
					<Button {...button} />
				</Box>
			)}

			<Divider mt={3} mb={5} />
		</>
	)
}
